import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { invoiceAPI, extractAPI, gstinAPI, settingsAPI, previewAPI, formatCurrency } from '../services/api';
import { toast } from 'sonner';
import { InvoiceHeader, VoucherTypeSelector } from '../components/invoice/InvoiceHeader';
import { InvoiceDetails } from '../components/invoice/InvoiceDetails';
import { PartyCards } from '../components/invoice/PartyCards';
import { LineItems } from '../components/invoice/LineItems';
import { TaxSummary } from '../components/invoice/TaxSummary';

const emptyLine = { description: '', hsn_sac: '996511', qty: 1, unit: 'NOS', list_price: 0, discount: 0 };

function round2(n) { return Math.round(n * 100) / 100; }

export default function InvoiceForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState('');
  const [autoGstEnabled, setAutoGstEnabled] = useState(false);
  const [advanceEngineEnabled, setAdvanceEngineEnabled] = useState(true);
  const [gstinFetching, setGstinFetching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [autoRound, setAutoRound] = useState(true);
  const [partyAdvances, setPartyAdvances] = useState([]);
  const [advanceBalance, setAdvanceBalance] = useState(0);

  const [form, setForm] = useState({
    voucher_type: 'sales',
    branch_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    invoice_number: '',
    gr_rr_no: '', transport: 'Road', vehicle_no: '', vehicle_type: '',
    place_of_supply: '', reverse_charge: 'No', station: '', bank_account: 'yes_bank',
    origin: '', destination: '',
    billed_to_name: '', billed_to_address: '', billed_to_gstin: '', billed_to_state: '',
    shipped_to_name: '', shipped_to_address: '', shipped_to_gstin: '', shipped_to_state: '',
    items: [{ ...emptyLine }], gst_rate: '', notes: '',
    terms: '1. Payment due within 30 days.\n2. Interest @ 18% p.a. on delayed payments.\n3. Subject to Delhi Jurisdiction.',
    round_off_manual: '',
    tds_enabled: false, tds_percentage: '', tds_section: '',
    advance_adjusted: 0, advance_ids: [],
  });

  const u = (f, v) => setForm(s => ({ ...s, [f]: v }));
  const isSales = form.voucher_type === 'sales';

  // ─── Load data on mount ───
  useEffect(() => {
    settingsAPI.bankAccounts().then(r => setBanks(r.data || [])).catch(() => {});
    settingsAPI.getAdvanceEngine().then(r => setAdvanceEngineEnabled(r.data?.enabled !== false)).catch(() => {});

    if (isEdit) {
      invoiceAPI.get(id).then(res => {
        const inv = res.data;
        const storedGst = Number(inv.gst_rate) || 0;
        setAutoGstEnabled(storedGst > 0);
        setForm({
          voucher_type: inv.voucher_type || 'sales',
          branch_id: inv.branch_id || '',
          invoice_number: inv.invoice_number || '',
          invoice_date: inv.invoice_date || '', gr_rr_no: inv.gr_rr_no || '', transport: inv.transport || 'Road',
          vehicle_no: inv.vehicle_no || '', vehicle_type: inv.vehicle_type || '', place_of_supply: inv.place_of_supply || '',
          reverse_charge: inv.reverse_charge || 'No', station: inv.station || '', bank_account: inv.bank_account || 'yes_bank',
          origin: inv.origin || '', destination: inv.destination || '',
          billed_to_name: inv.billed_to_name || '', billed_to_address: inv.billed_to_address || '',
          billed_to_gstin: inv.billed_to_gstin || '', billed_to_state: inv.billed_to_state || '',
          shipped_to_name: inv.shipped_to_name || '', shipped_to_address: inv.shipped_to_address || '',
          shipped_to_gstin: inv.shipped_to_gstin || '', shipped_to_state: inv.shipped_to_state || '',
          items: inv.items?.length ? inv.items.map(i => ({ description: i.description, hsn_sac: i.hsn_sac, qty: i.qty, unit: i.unit, list_price: i.list_price, discount: i.discount })) : [{ ...emptyLine }],
          gst_rate: storedGst, notes: inv.notes || '', terms: inv.terms || '',
          round_off_manual: inv.round_off !== undefined ? String(inv.round_off) : '',
          tds_enabled: inv.tds_enabled || false,
          tds_percentage: inv.tds_percentage ? String(inv.tds_percentage) : '',
          tds_section: inv.tds_section || '',
          advance_adjusted: inv.advance_adjusted || 0, advance_ids: inv.advance_ids || [],
        });
        if (inv.round_off !== undefined && inv.round_off !== 0) setAutoRound(false);
      }).catch(() => toast.error('Invoice not found'));
    } else {
      setAutoGstEnabled(false);
      setForm(prev => ({ ...prev, gst_rate: 0 }));
    }
  }, [id, isEdit]);

  // ─── Preview next invoice number (Bill Book aware) ───
  useEffect(() => {
    if (isEdit || !form.branch_id) return;
    previewAPI.nextInvoiceNumber(form.invoice_date, form.branch_id).then(r => setNextInvoiceNumber(r.data?.available ? r.data.next_number : '')).catch(() => {});
  }, [isEdit, form.invoice_date, form.branch_id]);

  // ─── Fetch advances for party ───
  const fetchAdvances = useCallback(async (partyName) => {
    if (!partyName || partyName.length < 2) { setPartyAdvances([]); setAdvanceBalance(0); return; }
    try {
      const advType = form.voucher_type === 'sales' ? 'received' : 'paid';
      const res = await api.get(`/advances/party/${encodeURIComponent(partyName)}`, { params: { type: advType } });
      setPartyAdvances(res.data.advances || []);
      setAdvanceBalance(res.data.total_balance || 0);
    } catch { setPartyAdvances([]); setAdvanceBalance(0); }
  }, [form.voucher_type]);

  useEffect(() => {
    if (form.billed_to_name) fetchAdvances(form.billed_to_name);
  }, [form.billed_to_name, form.voucher_type, fetchAdvances]);

  // ─── Item helpers ───
  const uItem = (idx, field, value) => {
    setForm(s => {
      const items = [...s.items];
      items[idx] = { ...items[idx], [field]: ['qty', 'list_price', 'discount'].includes(field) ? parseFloat(value) || 0 : value };
      return { ...s, items };
    });
  };
  const addItem = () => setForm(s => ({ ...s, items: [...s.items, { ...emptyLine }] }));
  const removeItem = (idx) => setForm(s => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));

  // ─── Computations ───
  const subtotal = form.items.reduce((s, it) => s + (it.list_price - it.discount) * it.qty, 0);
  const isIntra = (form.place_of_supply || form.shipped_to_state || '').toLowerCase().trim() === 'delhi' || (form.place_of_supply || form.shipped_to_state || '').toLowerCase().trim() === 'new delhi';
  const gstRate = autoGstEnabled ? (parseFloat(form.gst_rate) || 0) : 0;
  const cgst = autoGstEnabled && isIntra ? round2(subtotal * gstRate / 200) : 0;
  const sgst = autoGstEnabled && isIntra ? round2(subtotal * gstRate / 200) : 0;
  const igst = autoGstEnabled && !isIntra && (form.place_of_supply || form.shipped_to_state) ? round2(subtotal * gstRate / 100) : 0;
  const gstTotal = cgst + sgst + igst;
  const grossAmount = subtotal + gstTotal;
  const tdsPercentage = form.tds_enabled ? Math.min(Math.max(parseFloat(form.tds_percentage) || 0, -30), 30) : 0;
  const tdsAmount = form.tds_enabled ? round2(subtotal * Math.abs(tdsPercentage) / 100) : 0;
  const isTdsPayable = tdsPercentage < 0;
  const totalBeforeRound = grossAmount - tdsAmount;
  const roundOff = autoRound ? round2(Math.round(totalBeforeRound) - totalBeforeRound) : (parseFloat(form.round_off_manual) || 0);
  const grandTotal = round2(totalBeforeRound + roundOff);
  const advAdjusted = Math.min(Math.max(parseFloat(form.advance_adjusted) || 0, 0), grandTotal, advanceBalance);
  const netPayable = round2(grandTotal - advAdjusted);

  // ─── GSTIN lookup ───
  const handleGSTLookup = async (prefix, gstin) => {
    if (gstin.length !== 15) { toast.error('Enter complete 15-digit GSTIN'); return; }
    setGstinFetching(true);
    try {
      const res = await gstinAPI.validate(gstin);
      const d = res.data;
      if (d.valid) {
        const updates = {};
        if (d.trade_name) updates[`${prefix}_name`] = d.trade_name;
        else if (d.legal_name) updates[`${prefix}_name`] = d.legal_name;
        if (d.address) updates[`${prefix}_address`] = d.address;
        if (d.state) { updates[`${prefix}_state`] = d.state; if (prefix === 'billed_to') updates['place_of_supply'] = d.state; }
        setForm(prev => ({ ...prev, ...updates }));
        toast.success(`GSTIN verified: ${d.trade_name || d.legal_name || d.state || 'Valid'}`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'GSTIN lookup failed'); }
    setGstinFetching(false);
  };

  // ─── PDF upload ───
  const handlePDFUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await extractAPI.pdf('invoice', file);
      const d = res.data.extracted;
      if (d) { Object.entries(d).forEach(([k, v]) => { if (k === 'items') { if (Array.isArray(v) && v.length > 0) u(k, v); } else if (v) u(k, v); }); toast.success('Data extracted from PDF!'); }
    } catch { toast.error('PDF extraction failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  // ─── Save ───
  const handleSave = async () => {
    if (!form.billed_to_name) { toast.error(isSales ? 'Enter billed to name' : 'Enter vendor name'); return; }
    if (form.items.every(i => !i.description)) { toast.error('Add at least one item'); return; }
    setSaving(true);
    const payload = {
      ...form, gst_rate: gstRate, round_off_manual: autoRound ? null : (parseFloat(form.round_off_manual) || 0),
      tds_percentage: tdsPercentage, advance_adjusted: advAdjusted,
      advance_ids: partyAdvances.filter(a => a.balance_amount > 0).map(a => a.advance_id),
    };
    try {
      if (isEdit) { await invoiceAPI.update(id, payload); toast.success('Invoice updated'); navigate('/invoices'); }
      else { const res = await invoiceAPI.create(payload); toast.success(`${isSales ? 'Sales' : 'Purchase'} Invoice ${res.data.invoice_number} created`); navigate(`/invoices/${res.data.invoice_id}`); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const onToggleGst = () => {
    const next = !autoGstEnabled;
    setAutoGstEnabled(next);
    u('gst_rate', next ? (form.gst_rate && Number(form.gst_rate) > 0 ? form.gst_rate : 18) : 0);
  };

  return (
    <div data-testid="invoice-form-page" className="max-w-6xl mx-auto space-y-5">
      <InvoiceHeader isEdit={isEdit} isSales={isSales} nextInvoiceNumber={nextInvoiceNumber} uploading={uploading} onBack={() => navigate(-1)} onPDFUpload={handlePDFUpload} />
      <VoucherTypeSelector form={form} isSales={isSales} nextInvoiceNumber={nextInvoiceNumber} onChange={u} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <InvoiceDetails form={form} onChange={u} />
          <PartyCards form={form} isSales={isSales} gstinFetching={gstinFetching} onChange={u} onGSTLookup={handleGSTLookup} />
          <LineItems items={form.items} onUpdateItem={uItem} onAddItem={addItem} onRemoveItem={removeItem} />

          <div className="bg-white border border-slate-200 rounded-md p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="form-label">Notes</label><textarea value={form.notes} onChange={(e) => u('notes', e.target.value)} className="form-input min-h-[60px]" /></div>
            <div><label className="form-label">Terms & Conditions</label><textarea value={form.terms} onChange={(e) => u('terms', e.target.value)} className="form-input min-h-[60px]" /></div>
            <div><label className="form-label">Bank Details on PDF</label>
              <select value={form.bank_account} onChange={(e) => u('bank_account', e.target.value)} className="form-input" data-testid="bank-account-select">
                <option value="">No Bank Details</option>
                {banks.map(b => <option key={b.key} value={b.key}>{b.label} — A/C: {b.account}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div>
          <TaxSummary form={form} isSales={isSales} autoGstEnabled={autoGstEnabled} onToggleGst={onToggleGst} onChange={u}
            subtotal={subtotal} isIntra={isIntra} gstRate={gstRate} cgst={cgst} sgst={sgst} igst={igst} gstTotal={gstTotal} grossAmount={grossAmount}
            tdsPercentage={tdsPercentage} tdsAmount={tdsAmount} isTdsPayable={isTdsPayable}
            advanceEngineEnabled={advanceEngineEnabled} advanceBalance={advanceBalance} partyAdvances={partyAdvances} advAdjusted={advAdjusted}
            autoRound={autoRound} setAutoRound={setAutoRound} roundOff={roundOff} grandTotal={grandTotal} netPayable={netPayable}
            saving={saving} onSave={handleSave} />
        </div>
      </div>
    </div>
  );
}
